/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import { Box, Button, styled } from "@mui/material";
import React from "react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type UseToastProps = {
    message: string | Function;
    confirmMessage?: string;
    confirmCallback?: () => void;
    type?: ToastType;
    promise?: Promise<any>;
    duration?: number;
    position?: ToastPosition;
};

export enum ToastType {
    SUCCESS = 'success',
    ERROR = 'error',
    INFO = 'info',
    PROMISE = 'promise',
    CONFIRM = 'confirm',
}

export enum ToastPosition {
    TOP_RIGHT = 'top-right',
    TOP_LEFT = 'top-left',
    BOTTOM_RIGHT = 'bottom-right',
    BOTTOM_LEFT = 'bottom-left',
    TOP_CENTER = 'top-center',
}

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
}));

const useToast = () => {
    const { t } = useTranslation("common");

    const showToast = useCallback((props: UseToastProps) => {
        const {
            message,
            type = ToastType.INFO,
            promise,
            duration = 5000,
            position = ToastPosition.BOTTOM_RIGHT,
            confirmMessage,
            confirmCallback,
        } = props;

        const options = { duration, position };
        const messageContent = typeof message === "function" ? message() : message;
        const isReactComponent = typeof messageContent === 'object' && messageContent !== null && messageContent.$$typeof;

        if (type === ToastType.SUCCESS) {
            return toast.success(isReactComponent ? messageContent : <Box dangerouslySetInnerHTML={{ __html: messageContent }} />, options);
        } else if (type === ToastType.ERROR) {
            return toast.error(isReactComponent ? messageContent : <Box dangerouslySetInnerHTML={{ __html: messageContent }} />, options);
        } else if (type === ToastType.PROMISE && promise) {
            return toast.promise(
                promise,
                {
                    loading: t("common.processing"),
                    success: isReactComponent ? messageContent : <Box dangerouslySetInnerHTML={{ __html: messageContent }} />,
                    error: (err) => err ? err : t("common.errorOccurred"),
                },
                options,
            );
        } else if (type === ToastType.CONFIRM) {
            return toast((toastInstance) => (
                <Box>
                    <Box dangerouslySetInnerHTML={{ __html: confirmMessage ?? '' }} />
                    <ActionButtonsContainer>
                        <Button
                            color="success"
                            variant="outlined"
                            onClick={() => {
                                confirmCallback && confirmCallback();
                                toast.dismiss(toastInstance.id);
                            }}
                            aria-label="Dismiss"
                        >
                            {t("common.confirm")}
                        </Button>
                        <Button
                            color="error"
                            variant="outlined"
                            onClick={() => toast.dismiss(toastInstance.id)}
                        >
                            {t("common.dismiss")}
                        </Button>
                    </ActionButtonsContainer>
                </Box>
            ), {
                duration: Infinity,
            });
        } else {
            return toast(messageContent, options);
        }
    }, [t]);

    return { showToast };
};

export default useToast;
